/**
 * exportXlsx.ts — Branded XLSX export matching VISITOR_Weekly_Report_Central_BA.xlsx
 *
 * Layout (mirrors CentralWeeklyXlsxProcessor.java):
 *
 *  Rows 1–9  : Header block (logos skipped)
 *    row 3   : Report type banner  — A:lastCol merged, dark-red fill, white 24pt bold
 *    row 6   : "Shopping center :"  label (B:C) + value (D:H)
 *    row 7   : "Obdobie :"          label (B:C) + value (D:I)
 *    row 8   : "Číslo týždňa :"     label + value (weekly only)
 *    row 9   : bottom spacer
 *
 *  Per section (starting at row 10, continuous):
 *    row N+0   : Section title bar — col A left FREE, B:V (col 2–22) merged, dark-red
 *    rows N+1..N+22 : Chart image (22 rows, anchored cols D–S = 1-based 4–19)
 *    row  N+23 : Table header row 1
 *    row  N+24 : Table header row 2 ("Počet návštevníkov")
 *    rows N+25+: Data rows (alternating white / light-grey)
 *    (last)    : Totals/average footer row
 *    +2 rows   : gap before next section
 *
 *  Column layout (1-based) matches Java exactly:
 *
 *  "total" layout (first table in template, rows 36-43):
 *    B:C  = label + row# (merged B:C in header rows)
 *    D:F  = date-from  (3 cols)
 *    G:K  = date-to    (5 cols)
 *    L:O  = year[0] value  (4 cols)
 *    P:V  = YoY % vs year[1]  (7 cols)
 *
 *  "breakdown" layout (location/zone/day tables, Java columnIndex = 4+yi*3, 0-based → 1-based = 5+yi*3):
 *    col 2  B : row label
 *    col 3  C : row number
 *    col 5  E : year[0] value  merged E:G  (3 cols)
 *    col 8  H : year[1] value  merged H:J
 *    col 11 K : year[2] value  merged K:M
 *    col 14 N : year[3] value  merged N:P
 *    col 17 Q : year[4] value  merged Q:S
 *    col 20 T : year[5] value  merged T:V  (last always extends to col 22 = V)
 *
 * Charts: PNG via QuickChart.io / ApexCharts (same endpoint as Java ApexGraphExporter).
 *         Grouped-bar for multi-year; distributed single-series bar for single year.
 *
 * Build: exceljs + file-saver are in package.json — installed in Docker only.
 */

// @ts-ignore — installed in Docker; skipLibCheck=true in tsconfig
import ExcelJS from 'exceljs';
// @ts-ignore
import { saveAs } from 'file-saver';
import { IReportBreakdownRow } from './reportHelpers';

// ─── Brand colours ──────────────────────────────────────────────────────────────
const C = {
    sectionBg: 'FF800000',   // dark red
    sectionFg: 'FFFFFFFF',   // white
    headerBg:  'FFD9D9D9',   // light grey header
    headerFg:  'FF000000',
    yearFills: [
        'FFFFFFFF',  // yr0 white
        'FFFFF2CC',  // yr1 light yellow
        'FFE2EFDA',  // yr2 light green
        'FFDAE3F3',  // yr3 light blue
        'FFFCE4D6',  // yr4 light orange
        'FFEDEDED',  // yr5 light grey
    ] as const,
    altFill:  'FFF2F2F2',
    totalsBg: 'FFD9D9D9',
    yoyPosBg: 'FFE2EFDA',
    yoyNegBg: 'FFFCE4D6',
    yoyPos:   'FF1B7A3E',
    yoyNeg:   'FFC0392B',
    border:   'FF000000',
} as const;

// ─── Border helpers ─────────────────────────────────────────────────────────────
function bD(): Partial<ExcelJS.Border> { return { style: 'double', color: { argb: C.border } }; }
function bT(): Partial<ExcelJS.Border> { return { style: 'thin',   color: { argb: C.border } }; }
function bordAll(s: 'double' | 'thin'): Partial<ExcelJS.Borders> {
    const b = s === 'double' ? bD() : bT();
    return { top: b, left: b, bottom: b, right: b };
}
function bordMixed(
    top: 'double' | 'thin' | null, right: 'double' | 'thin' | null,
    bot: 'double' | 'thin' | null, left: 'double' | 'thin' | null,
): Partial<ExcelJS.Borders> {
    const mk = (s: 'double' | 'thin' | null) =>
        s ? (s === 'double' ? bD() : bT()) : undefined;
    return { top: mk(top) as any, right: mk(right) as any, bottom: mk(bot) as any, left: mk(left) as any };
}

// ─── Style factories ────────────────────────────────────────────────────────────

function styleSection(): Partial<ExcelJS.Style> {
    return {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: C.sectionBg } },
        font: { bold: true, size: 12, color: { argb: C.sectionFg }, name: 'Tahoma' },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: bordAll('double'),
    };
}

function styleHdr(fill: string = C.headerBg): Partial<ExcelJS.Style> {
    return {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } },
        font: { bold: true, size: 11, color: { argb: C.headerFg }, name: 'Calibri' },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: bordAll('double'),
    };
}

function styleLabel(fill: string): Partial<ExcelJS.Style> {
    return {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } },
        font: { bold: true, size: 11, color: { argb: 'FF000000' }, name: 'Calibri' },
        alignment: { horizontal: 'left', vertical: 'middle', wrapText: true, indent: 1 },
        border: bordAll('double'),
    };
}

function styleNum(fill: string): Partial<ExcelJS.Style> {
    return {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } },
        font: { bold: true, size: 11, color: { argb: 'FF000000' }, name: 'Calibri' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: bordAll('double'),
    };
}

function styleData(fill: string): Partial<ExcelJS.Style> {
    return {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } },
        font: { size: 11, color: { argb: 'FF000000' }, name: 'Calibri' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        numFmt: '# ##0',
        border: bordMixed('double', 'double', 'thin', 'thin'),
    };
}

function styleTotLabel(): Partial<ExcelJS.Style> {
    return {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: C.totalsBg } },
        font: { bold: true, size: 11, color: { argb: 'FF000000' }, name: 'Calibri' },
        alignment: { horizontal: 'left', vertical: 'middle', indent: 1 },
        border: bordAll('double'),
    };
}

function styleTotData(fill: string = C.totalsBg): Partial<ExcelJS.Style> {
    return {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } },
        font: { bold: true, size: 11, color: { argb: 'FF000000' }, name: 'Calibri' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        numFmt: '# ##0',
        border: bordAll('double'),
    };
}

function styleYoy(pct: number): Partial<ExcelJS.Style> {
    const pos = pct >= 0;
    return {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: pos ? C.yoyPosBg : C.yoyNegBg } },
        font: { bold: true, size: 10, italic: true, color: { argb: pos ? C.yoyPos : C.yoyNeg }, name: 'Calibri' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        numFmt: '+0.0%;-0.0%',
        border: bordAll('double'),
    };
}

function styleInfo(bold = false): Partial<ExcelJS.Style> {
    return {
        font: { bold, size: 12, color: { argb: 'FF000000' }, name: 'Calibri' },
        alignment: { horizontal: 'left', vertical: 'middle' },
    };
}

// ─── Column constants (all 1-based) ────────────────────────────────────────────
const LAST_COL  = 22;   // col V — fixed sheet width
const COL_A     =  1;
const COL_B     =  2;   // row label
const COL_C     =  3;   // row number

// "total" layout columns (mirrors Java first table: D=date-from, G=date-to, L=value, P=YoY):
const COL_DF    =  4;   // D  date-from start
const COL_DF_E  =  6;   // F  date-from end   (3 cols: D:F)
const COL_DT    =  7;   // G  date-to start
const COL_DT_E  = 11;   // K  date-to end     (5 cols: G:K)
const COL_V0    = 12;   // L  value yr0 start
const COL_V0_E  = 15;   // O  value yr0 end   (4 cols: L:O)
const COL_YOY   = 16;   // P  YoY start
const COL_YOY_E = 22;   // V  YoY end         (7 cols: P:V)

// "breakdown" layout: year[yi] value starts at col (5 + yi*3), width 3, last stretches to V
function bkValStart(yi: number): number { return 5 + yi * 3; }
function bkValEnd(yi: number, nYears: number): number {
    return yi === nYears - 1 ? LAST_COL : 4 + yi * 3 + 3;
}

// ─── Merge + style helper ───────────────────────────────────────────────────────
function mc(
    ws: ExcelJS.Worksheet,
    row: number, c1: number, c2: number,
    value: ExcelJS.CellValue,
    style: Partial<ExcelJS.Style>,
) {
    if (c2 > c1) ws.mergeCells(row, c1, row, c2);
    const cell = ws.getCell(row, c1);
    cell.value = value;
    cell.style = style as ExcelJS.Style;
}

// ─── Column widths ──────────────────────────────────────────────────────────────
function applyWidths(ws: ExcelJS.Worksheet) {
    ws.getColumn(COL_A).width = 3;
    ws.getColumn(COL_B).width = 28;
    ws.getColumn(COL_C).width = 5;
    for (let c = 4;  c <= 6;  c++) ws.getColumn(c).width = 9;   // D:F
    for (let c = 7;  c <= 11; c++) ws.getColumn(c).width = 9;   // G:K
    for (let c = 12; c <= LAST_COL; c++) ws.getColumn(c).width = 11; // L:V
}

// ─── Chart generation (QuickChart.io / ApexCharts) ─────────────────────────────
const QUICKCHART_URL    = 'https://quickchart.io/apex-charts/render';
const YEAR_CHART_COLORS = [
    '#800000', '#BF8F00', '#548235', '#2F75B5', '#993399', '#808080',
];

async function fetchChartPng(
    categories: string[],
    series: { name: string; color: string; data: number[] }[],
    title: string,
    width = 1100,
    height = 480,
): Promise<ArrayBuffer | undefined> {
    try {
        const distributed = series.length === 1;
        const colors = distributed
            ? categories.map((_, i) => YEAR_CHART_COLORS[i % YEAR_CHART_COLORS.length])
            : series.map(s => s.color);

        const config = {
            chart: {
                type: 'bar', height, width,
                toolbar: { show: false },
                animations: { enabled: false },
            },
            plotOptions: {
                bar: {
                    borderRadius: 4,
                    distributed,
                    dataLabels: { position: 'top' },
                    columnWidth: distributed ? '60%' : '80%',
                },
            },
            dataLabels: {
                enabled: true,
                offsetY: -20,
                style: { fontSize: '11px', colors: ['#304758'] },
                formatter: 'function(val){return val>0?val.toLocaleString("sk-SK"):"";}',
            },
            stroke: { show: true, width: 2, colors: ['transparent'] },
            xaxis: { categories, labels: { style: { fontSize: '10px' } } },
            yaxis: { title: { text: 'Počet návštevníkov' } },
            fill: { opacity: 1 },
            legend: { show: !distributed, position: 'top' },
            title: { text: title, style: { fontSize: '13px', fontWeight: 'bold', fontFamily: 'Calibri' } },
            colors,
            series: series.map(s => ({ name: s.name, data: s.data })),
            grid: { show: true, xaxis: { lines: { show: false } } },
        };

        const params = new URLSearchParams({ config: JSON.stringify(config), height: String(height) });
        const res = await fetch(`${QUICKCHART_URL}?${params.toString()}`);
        if (!res.ok) { console.warn('[exportXlsx] QuickChart HTTP', res.status); return undefined; }
        return await res.arrayBuffer();
    } catch (e) {
        console.warn('[exportXlsx] QuickChart error:', e);
        return undefined;
    }
}

// ─── Header block (rows 1-9) ───────────────────────────────────────────────────
function writeHeader(
    ws: ExcelJS.Worksheet,
    reportType: string,
    martName: string,
    dateLabel: string,
    weekNum?: number,
    startDate?: string,
) {
    // Row 1 — logo placeholder / top margin
    ws.getRow(1).height = 20;

    // Row 2 — spacer
    ws.getRow(2).height = 8;

    // Row 3 — report type banner: A:V merged, dark red
    ws.getRow(3).height = 45;
    mc(ws, 3, COL_A, LAST_COL, reportType.toUpperCase(), {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: C.sectionBg } },
        font: { bold: true, size: 24, color: { argb: C.sectionFg }, name: 'Calibri' },
        alignment: { horizontal: 'center', vertical: 'middle' },
    });

    // Rows 4–5 — spacers
    ws.getRow(4).height = 8;
    ws.getRow(5).height = 8;

    // Row 6 — Shopping center
    ws.getRow(6).height = 20;
    mc(ws, 6, COL_B, COL_C, 'Shopping center :', styleInfo());
    mc(ws, 6, COL_B + 2, COL_B + 5, martName, styleInfo(true));

    // Row 7 — Period
    ws.getRow(7).height = 20;
    mc(ws, 7, COL_B, COL_C, 'Obdobie :', styleInfo());
    mc(ws, 7, COL_B + 2, COL_B + 7, dateLabel, styleInfo(true));

    // Row 8 — Week number / start date (weekly only)
    ws.getRow(8).height = 20;
    if (weekNum !== undefined) {
        mc(ws, 8, COL_B, COL_C, 'Číslo týždňa :', styleInfo());
        ws.getCell(8, COL_B + 2).value = `${weekNum}.`;
        ws.getCell(8, COL_B + 2).style = styleInfo(true) as ExcelJS.Style;
    }
    if (startDate) {
        mc(ws, 8, COL_B + 4, COL_B + 5, 'Začiatok :', styleInfo());
        ws.getCell(8, COL_B + 6).value = startDate;
        ws.getCell(8, COL_B + 6).style = styleInfo(true) as ExcelJS.Style;
    }

    // Row 9 — bottom spacer of header
    ws.getRow(9).height = 8;
}

// ─── Section title bar ──────────────────────────────────────────────────────────
/** Dark-red title bar.  Col A is intentionally left blank (no fill). */
function writeSectionBar(ws: ExcelJS.Worksheet, r: number, title: string): number {
    ws.getRow(r).height = 22;
    ws.getCell(r, COL_A).value = '';   // col A left free
    mc(ws, r, COL_B, LAST_COL, title, styleSection());
    return r + 1;
}

// ─── Chart area ─────────────────────────────────────────────────────────────────
const CHART_ROWS  = 22;
const CHART_ROW_H = 16;
const CHART_W     = 1100;
const CHART_H     = 480;

async function writeChartArea(
    ws: ExcelJS.Worksheet,
    wb: ExcelJS.Workbook,
    r: number,
    section: IXlsxSection,
    columns: { year: number; label: string }[],
): Promise<number> {
    const chartStart = r;
    const chartEnd   = r + CHART_ROWS - 1;

    for (let cr = chartStart; cr <= chartEnd; cr++) ws.getRow(cr).height = CHART_ROW_H;

    const categories = section.rows.map(row => row.label);
    const series = columns.map((col, yi) => ({
        name:  col.label,
        color: YEAR_CHART_COLORS[yi] ?? '#808080',
        data:  section.rows.map(row => row.values.find(v => v.year === col.year)?.value ?? 0),
    }));

    const pngBuffer = await fetchChartPng(categories, series, section.title, CHART_W, CHART_H);

    if (pngBuffer) {
        try {
            const uint8 = new Uint8Array(pngBuffer);
            // @ts-ignore — exceljs accepts Uint8Array at runtime
            const imgId = wb.addImage({ buffer: uint8, extension: 'png' });
            // tl/br are 0-based in ExcelJS addImage:
            //   col D = 1-based 4 → 0-based 3
            //   col S = 1-based 19 → 0-based 18
            // Cast to any: ExcelJS TS types require nativeCol/nativeRow on Anchor,
            // but the plain {col, row} form works fine at runtime.
            ws.addImage(imgId, {
                tl: { col: COL_DF - 1, row: chartStart - 1 },
                br: { col: 19,         row: chartEnd },
                editAs: 'oneCell',
            } as any);
        } catch (e) {
            console.warn('[exportXlsx] image embed error:', e);
        }
    }

    return chartEnd + 1;
}

// ─── "total" table (first/summary section) ────────────────────────────────────
// Mirrors template rows 36-43: B:C label, D:F date-from, G:K date-to, L:O value, P:V YoY
function writeTotalTable(
    ws: ExcelJS.Worksheet,
    r: number,
    columns: { year: number; label: string }[],
    rows: IReportBreakdownRow[],
): number {
    const nYears = columns.length;
    const y0fill = (C.yearFills as readonly string[])[0];
    const y1fill = (C.yearFills as readonly string[])[1] ?? C.headerBg;

    // ── Header row 1
    ws.getRow(r).height = 20;
    mc(ws, r, COL_B, COL_C, 'Poradie', styleHdr());
    mc(ws, r, COL_DF,  COL_DF_E, 'Od',    styleHdr());
    mc(ws, r, COL_DT,  COL_DT_E, 'Do',    styleHdr());
    mc(ws, r, COL_V0,  COL_V0_E, columns[0]?.label ?? '', styleHdr(y0fill));
    mc(ws, r, COL_YOY, COL_YOY_E, nYears > 1 ? `vs ${columns[1]?.label ?? ''}` : '', styleHdr(nYears > 1 ? y1fill : C.headerBg));
    r++;

    // ── Header row 2
    ws.getRow(r).height = 18;
    mc(ws, r, COL_B,  COL_C,     '', styleHdr());
    mc(ws, r, COL_DF, COL_DF_E,  '', styleHdr());
    mc(ws, r, COL_DT, COL_DT_E,  '', styleHdr());
    mc(ws, r, COL_V0, COL_V0_E,  'Počet návštevníkov', styleHdr(y0fill));
    mc(ws, r, COL_YOY, COL_YOY_E, nYears > 1 ? '% zmena' : '', styleHdr(nYears > 1 ? y1fill : C.headerBg));
    r++;

    // ── Data rows
    rows.forEach((row, ri) => {
        const fill = (C.yearFills as readonly string[])[ri % C.yearFills.length] ?? 'FFFFFFFF';
        ws.getRow(r).height = 18;
        ws.getCell(r, COL_A).value = '';

        mc(ws, r, COL_B, COL_C, row.label, styleLabel(fill));

        const dateFrom = (row as any).dateFrom ?? '';
        const dateTo   = (row as any).dateTo   ?? '';
        mc(ws, r, COL_DF,  COL_DF_E, dateFrom, styleData(fill));
        mc(ws, r, COL_DT,  COL_DT_E, dateTo,   styleData(fill));

        const v0 = row.values.find(v => v.year === columns[0]?.year)?.value ?? 0;
        mc(ws, r, COL_V0, COL_V0_E, v0, styleData(fill));

        if (nYears > 1) {
            const v1 = row.values.find(v => v.year === columns[1]?.year)?.value ?? 0;
            if (v1 > 0) {
                mc(ws, r, COL_YOY, COL_YOY_E, (v0 / v1) - 1, styleYoy((v0 / v1) - 1));
            } else {
                mc(ws, r, COL_YOY, COL_YOY_E, '–', styleHdr('FFEEEEEE'));
            }
        } else {
            mc(ws, r, COL_YOY, COL_YOY_E, '', styleData(fill));
        }
        r++;
    });

    // ── Totals
    if (rows.length > 1) {
        ws.getRow(r).height = 20;
        ws.getCell(r, COL_A).value = '';
        mc(ws, r, COL_B, COL_C, 'Spolu', styleTotLabel());
        mc(ws, r, COL_DF, COL_DF_E, '', styleTotData());
        mc(ws, r, COL_DT, COL_DT_E, '', styleTotData());
        const tot0 = rows.reduce((s, row) => s + (row.values.find(v => v.year === columns[0]?.year)?.value ?? 0), 0);
        mc(ws, r, COL_V0, COL_V0_E, tot0, styleTotData());
        if (nYears > 1) {
            const tot1 = rows.reduce((s, row) => s + (row.values.find(v => v.year === columns[1]?.year)?.value ?? 0), 0);
            mc(ws, r, COL_YOY, COL_YOY_E, tot1 > 0 ? (tot0 / tot1) - 1 : '–',
                tot1 > 0 ? styleYoy((tot0 / tot1) - 1) : styleTotData());
        } else {
            mc(ws, r, COL_YOY, COL_YOY_E, '', styleTotData());
        }
        r++;
    }

    return r;
}

// ─── "breakdown" table (location / zone / day tables) ─────────────────────────
// Col layout: B=label, C=#, then E,H,K,N,Q,T (1-based 5+yi*3) per year, last to V.
// Mirrors Java second/third/fourth table column layout (columnIndex = 4+yi*3, 0-based).
function writeBreakdownTable(
    ws: ExcelJS.Worksheet,
    r: number,
    columns: { year: number; label: string }[],
    rows: IReportBreakdownRow[],
    footerLabel = 'Spolu',
    showFooter = true,
): number {
    const nYears = columns.length;

    // ── Header row 1 — year labels
    ws.getRow(r).height = 20;
    mc(ws, r, COL_B, COL_B, 'Názov',  styleHdr());
    mc(ws, r, COL_C, COL_C, 'č.',     styleHdr());
    columns.forEach((col, yi) => {
        const sc   = bkValStart(yi);
        const ec   = bkValEnd(yi, nYears);
        const fill = (C.yearFills as readonly string[])[yi] ?? 'FFFFFFFF';
        mc(ws, r, sc, ec, col.label, styleHdr(fill));
    });
    r++;

    // ── Header row 2 — "Počet návštevníkov"
    ws.getRow(r).height = 18;
    mc(ws, r, COL_B, COL_B, '', styleHdr());
    mc(ws, r, COL_C, COL_C, '', styleHdr());
    columns.forEach((_col, yi) => {
        const sc   = bkValStart(yi);
        const ec   = bkValEnd(yi, nYears);
        const fill = (C.yearFills as readonly string[])[yi] ?? 'FFFFFFFF';
        mc(ws, r, sc, ec, 'Počet návštevníkov', styleHdr(fill));
    });
    r++;

    // ── Data rows
    const totals = new Array(nYears).fill(0);
    rows.forEach((row, ri) => {
        const fill = ri % 2 === 0 ? 'FFFFFFFF' : C.altFill;
        ws.getRow(r).height = 18;
        ws.getCell(r, COL_A).value = '';
        ws.getCell(r, COL_B).value = row.label;
        ws.getCell(r, COL_B).style = styleLabel(fill) as ExcelJS.Style;
        ws.getCell(r, COL_C).value = ri + 1;
        ws.getCell(r, COL_C).style = styleNum(fill) as ExcelJS.Style;

        columns.forEach((col, yi) => {
            const sc   = bkValStart(yi);
            const ec   = bkValEnd(yi, nYears);
            const yfill = (C.yearFills as readonly string[])[yi] ?? fill;
            const v    = row.values.find(val => val.year === col.year)?.value ?? 0;
            totals[yi] += v;
            mc(ws, r, sc, ec, v, styleData(yfill));
        });
        r++;
    });

    // ── Footer
    if (showFooter && rows.length > 1) {
        ws.getRow(r).height = 20;
        ws.getCell(r, COL_A).value = '';
        ws.getCell(r, COL_B).value = footerLabel;
        ws.getCell(r, COL_B).style = styleTotLabel() as ExcelJS.Style;
        ws.getCell(r, COL_C).value = '';
        ws.getCell(r, COL_C).style = styleTotData() as ExcelJS.Style;
        columns.forEach((_col, yi) => {
            const sc = bkValStart(yi);
            const ec = bkValEnd(yi, nYears);
            mc(ws, r, sc, ec, totals[yi], styleTotData());
        });
        r++;
    }

    return r;
}

// ─── Top-days table ─────────────────────────────────────────────────────────────
function writeTopDays(
    ws: ExcelJS.Worksheet,
    r: number,
    title: string,
    days: IXlsxTopDay[],
): number {
    if (!days || days.length === 0) return r;
    r = writeSectionBar(ws, r, title);

    // Header
    ws.getRow(r).height = 18;
    mc(ws, r, COL_B,     COL_B,         '#',           styleHdr());
    mc(ws, r, COL_C,     COL_C + 3,     'Dátum',        styleHdr());
    mc(ws, r, COL_C + 4, COL_C + 7,     'Deň',          styleHdr());
    mc(ws, r, COL_C + 8, LAST_COL,      'Návštevnosť',  styleHdr());
    r++;

    days.forEach((day, di) => {
        const fill = di % 2 === 0 ? 'FFFFFFFF' : C.altFill;
        ws.getRow(r).height = 18;
        ws.getCell(r, COL_A).value = '';
        ws.getCell(r, COL_B).value = di + 1;
        ws.getCell(r, COL_B).style = styleNum(fill) as ExcelJS.Style;
        mc(ws, r, COL_C,     COL_C + 3, day.date,           styleLabel(fill));
        mc(ws, r, COL_C + 4, COL_C + 7, day.weekday ?? '',  styleData(fill));
        mc(ws, r, COL_C + 8, LAST_COL,  day.total,          styleData(fill));
        r++;
    });

    ws.getRow(r).height = 10;
    r++;
    return r;
}

// ─── Public types ───────────────────────────────────────────────────────────────

export interface IXlsxSection {
    title: string;
    rows: IReportBreakdownRow[];
    /** false = skip chart (default: true) */
    showChart?: boolean;
    /**
     * 'total'     — first/summary table: B:C label, D:F date-from, G:K date-to, L:O value, P:V YoY
     * 'breakdown' — location/zone/day:  B label, C #, then E/H/K/N/Q/T value cols per year
     * Default: 'breakdown'
     */
    layout?: 'total' | 'breakdown';
    /** Footer row label (default 'Spolu') */
    footerLabel?: string;
    /**
     * ApexCharts chart `id` for this section (set on the <Chart> `options.chart.id` prop).
     * Used by exportReportPdf to capture the live chart image via window.ApexCharts.exec.
     */
    chartId?: string;
}

export interface IXlsxTopDay {
    date: string;
    weekday?: string;
    total: number;
}

export interface IXlsxReportInput {
    martName:      string;
    reportType:    string;
    dateLabel:     string;
    weekNum?:      number;
    startDate?:    string;
    columns:       { year: number; label: string }[];
    sections:      IXlsxSection[];
    topDays?:      IXlsxTopDay[];
    topDaysTitle?: string;
    // kpis kept for backwards compat — not written to XLSX (shown only in UI)
    kpis?: any[];
    // sections rendered AFTER topDays in the PDF (ignored by XLSX exporter)
    trailingSections?: IXlsxSection[];
}

// ─── Main ───────────────────────────────────────────────────────────────────────

export async function exportReportXlsx(input: IXlsxReportInput): Promise<void> {
    const { martName, reportType, dateLabel, weekNum, startDate, columns, sections, topDays, topDaysTitle } = input;

    const wb = new ExcelJS.Workbook();
    wb.creator  = 'VISITOR Portal';
    wb.created  = new Date();
    wb.modified = new Date();

    const ws = wb.addWorksheet(reportType.substring(0, 31), {
        pageSetup: {
            paperSize: 9, orientation: 'landscape',
            fitToPage: true, fitToWidth: 1, fitToHeight: 0,
            margins: { left: 0.4, right: 0.4, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 },
        },
        properties: { tabColor: { argb: C.sectionBg } },
    });

    applyWidths(ws);
    ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 9 }];

    // ── Header (rows 1-9)
    writeHeader(ws, reportType, martName, dateLabel, weekNum, startDate);

    // ── Sections
    let r = 10;

    for (const section of sections) {
        if (!section.rows || section.rows.length === 0) continue;

        const showChart = section.showChart !== false;
        const layout    = section.layout ?? 'breakdown';

        // 1. Section title bar (col A free)
        r = writeSectionBar(ws, r, section.title);

        // 2. Chart image
        if (showChart) {
            r = await writeChartArea(ws, wb, r, section, columns);
        }

        // 3. Table
        if (layout === 'total') {
            r = writeTotalTable(ws, r, columns, section.rows);
        } else {
            r = writeBreakdownTable(ws, r, columns, section.rows,
                section.footerLabel ?? 'Spolu',
                section.rows.length > 1);
        }

        // 4. Gap between sections
        ws.getRow(r).height = 8; r++;
        ws.getRow(r).height = 8; r++;
    }

    // ── Top-days table
    if (topDays && topDays.length > 0) {
        r = writeTopDays(ws, r, topDaysTitle ?? 'Top 10 dní s najvyššou návštevnosťou', topDays);
    }

    // ── Download
    const safeDate = dateLabel.replace(/[/:*?[\]\\]/g, '-').substring(0, 40);
    const safeMart = martName.replace(/[/:*?[\]\\]/g, '-');
    const filename = `VISITOR_${safeMart}_${safeDate}.xlsx`;

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer as BlobPart], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }), filename);
}
